import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AssetMediaRef, WorkflowRunStep } from '@digital-twins/shared-types'

import { CreditIcon } from '@/components/icons/CreditIcon'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import i18n from '@/i18n'
import { WorkflowGateSubmission } from '@/lib/workflows/useWorkflowRun'

import { hasDuplicateStimulusMedia } from '../stimulusRefsFromRun'
import {
  GateArtifactEntry,
  isGateQuestionsDockBody,
  parseGateQuestions,
} from './gateArtifacts'
import { RunGateArtifactCard } from './RunGateArtifactCard'
import {
  computeRunGateCreditCost,
  RunGateSpendSummary,
} from './runSpendLine'

/**
 * The approval gate: where a person decides whether to spend money on twins.
 *
 * ONE PAYLOAD, ENFORCED BY SHAPE. Edits live in a `GateEditState` and nowhere
 * else, and the only prop that can reach the server is `onSubmit`, called
 * exactly once from `handleApprove` with every edit inside it. There is no
 * per-field save callback to pass in, so "submitted one field early" is not a
 * bug that can be introduced here without adding a prop, which is the point.
 * `Save` on a card closes an editor; it does not travel. Lifting the edits out
 * of this component changed WHO HOLDS the map and nothing about how it leaves:
 * a chat rewrite stages into the same map an editor writes to, and still waits
 * for the one approve.
 *
 * After the decision the gate stops being a control and becomes a record:
 * `Approved · 2 edits`, read-only, still showing what was reviewed.
 *
 * THE ACTIONABLE MOUNT HIDES PROSE. Briefs and drafts already sit in the
 * thread, so Continue does not replay those bodies. Questions payloads stay
 * visible — all of them, still editable — because `/test` reviews the
 * instrument before spend, and that preview is the one case that still docks
 * on the composer. One Approve submits the same single payload.
 *
 * WHERE THE DECISION IS TAKEN. The card renders in two places and decides in
 * one: `actionable` is true only for the copy mounted in the thread itself
 * (`ThreadRunGate`), where the reader is already reading. The run pane's copy
 * passes `actionable={false}` and is a watch-only record of what is waiting,
 * because a drawer the reader may have closed is the wrong place to keep the
 * one control that spends their money.
 */

/**
 * The gate's staged bodies: what the reviewer has changed but not yet sent,
 * plus which card is open in its editor.
 *
 * LIFTED OUT OF THE CARD ON PURPOSE. The same card mounts twice, once
 * actionable in the thread and once watch-only in the run pane, and while this
 * was private `useState` there were two independent maps, so nothing outside
 * the card could stage into the copy that decides. The owner of the deciding
 * mount holds one of these and passes it in; a mount that is handed nothing
 * falls back to its own, which is exactly the watch-only copy, whose map stays
 * empty because it cannot edit.
 *
 * It still cannot reach the server: there is no save callback anywhere in here.
 */
export interface GateEditState {
  /** Artifact key to replacement body. An absent key means untouched. */
  edits: Record<string, string>
  /** The card whose editor is open, if any. */
  editingKey: string | null
  startEdit: (key: string) => void
  cancelEdit: () => void
  saveEdit: (artifact: GateArtifactEntry, body: string) => void
  /** Stages a body without closing an editor. Chat rewrite and live fields use this. */
  stageEdit: (artifact: GateArtifactEntry, body: string) => void
  /**
   * Stages a full replacement set over the current cards and returns the ones
   * it SKIPPED: a card being edited by hand keeps the half-typed draft, and the
   * caller names it. `bodies` is positional and must already match `artifacts`
   * in length; nothing here pads or truncates.
   */
  applyRewrite: (
    artifacts: Array<GateArtifactEntry>,
    bodies: Array<string>,
  ) => Array<GateArtifactEntry>
}


/** Typed back to the original is not an edit: drop the entry rather than send it. */
function stageBody(
  current: Record<string, string>,
  key: string,
  original: string,
  body: string,
): Record<string, string> {
  if (body === original) {
    const next = { ...current }
    delete next[key]
    return next
  }
  return { ...current, [key]: body }
}

export function useGateEditState(resetKey: string | null): GateEditState {
  const [edits, setEdits] = useState<Record<string, string>>({})
  const [editingKey, setEditingKey] = useState<string | null>(null)

  // A second gate in the same run asks a different question. Bodies staged
  // against the first one must not survive into it.
  useEffect(() => {
    setEdits({})
    setEditingKey(null)
  }, [resetKey])

  function saveEdit(artifact: GateArtifactEntry, body: string) {
    setEdits((current) => stageBody(current, artifact.key, artifact.body, body))
    setEditingKey(null)
  }

  function stageEdit(artifact: GateArtifactEntry, body: string) {
    setEdits((current) => stageBody(current, artifact.key, artifact.body, body))
  }

  function applyRewrite(
    artifacts: Array<GateArtifactEntry>,
    bodies: Array<string>,
  ): Array<GateArtifactEntry> {
    const skipped = artifacts.filter((artifact) => artifact.key === editingKey)

    setEdits((current) => {
      let next = current
      artifacts.forEach((artifact, position) => {
        if (artifact.key === editingKey) {
          return
        }
        next = stageBody(next, artifact.key, artifact.body, bodies[position])
      })
      return next
    })

    return skipped
  }

  return {
    edits,
    editingKey,
    startEdit: setEditingKey,
    cancelEdit: () => setEditingKey(null),
    saveEdit,
    stageEdit,
    applyRewrite,
  }
}

interface RunGateCardProps {
  step: WorkflowRunStep
  artifacts: Array<GateArtifactEntry>
  /**
   * Whether THIS copy of the card carries the decision. False makes it a
   * watch-only record: same artifacts, same spend line, no editing and no
   * buttons, pointing at the copy in the thread that does decide.
   */
  actionable: boolean
  /** Template `editable` AND a viewport wide enough to edit in. */
  canEdit: boolean
  /**
   * `run.viewerOwnsRun`. Only the run's owner may act in v1: the server's
   * `requireOwnedRun` answers a colleague's approve with a 403, so the controls
   * are disabled rather than offered and then refused.
   */
  viewerOwnsRun: boolean
  /** Reads off the run: what approving actually sets in motion. */
  spendLine: string
  /**
   * Staged bodies held by somebody ELSE, so a rewrite arriving from the thread
   * lands in the copy that decides. Omit it and the card keeps its own, which
   * is right for the watch-only mount and for anything that never revises.
   */
  editState?: GateEditState
  /**
   * Optional QS credit chip (same parent/read basis as `spendLine`). Caller
   * builds via `buildRunGateCreditChip` — docked gate wires audience tier.
   */
  creditChip?: ReactNode
  /**
   * "Who answers" chips for a filtered quick-study run (`RunGateSegmentChips`),
   * rendered above the decision row. Omitted for a full-audience run.
   */
  segmentSlot?: ReactNode
  /**
   * True when the run's segment is below the respondent minimum: the reason
   * line lives in `segmentSlot`, and Approve stays disabled until it is fixed.
   */
  approveBlocked?: boolean
  /**
   * Bound `run.inputs.stimulus` refs. Media-compare Versions + Approve block
   * when N≥2 and the same public URL appears twice.
   */
  stimulusRefs?: Array<AssetMediaRef>
  /**
   * Peer-question ceiling for the twin-test gate's Add-question button.
   * Caller resolves this off the run's own template
   * (`twinTestGateMaxQuestions`); omitted it falls back to
   * `TWIN_TEST_MAX_QUESTIONS` inside `RunGateQuestionList`.
   */
  maxQuestions?: number
  /**
   * `card` (default) is the bordered watch/composer shell with a sticky
   * footer. `inline` is the thread prose gate: no muted card, no sticky
   * composer-surface footer — Continue / Cancel / spend match creative Confirm.
   */
  chrome?: 'card' | 'inline'
  /**
   * The step that runs once this gate is approved, already localized. A
   * review-only gate labels its button with it ("Write the morning brief"),
   * so the button says what happens next instead of a bare Continue. Spend
   * gates keep Approve and run: the spend line is the disclosure there.
   */
  nextStepTitle?: string | null
  /** Study / thread content language for seeded option labels. */
  language?: string | null
  onSubmit: (submission: WorkflowGateSubmission) => void
  onCancelRun: () => void
}

function decisionSummary(step: WorkflowRunStep): string {
  const output = step.output
  const edits =
    output && output.kind === 'gate' ? output.editedArtifactCount : 0
  const decidedKey =
    step.gateDecision === 'reject'
      ? 'threads.detail.gate.rejected'
      : 'threads.detail.gate.approved'

  if (edits === 0) {
    return i18n.t(decidedKey, { ns: 'app' })
  }
  return i18n.t(
    step.gateDecision === 'reject'
      ? 'threads.detail.gate.rejectedEdits'
      : 'threads.detail.gate.approvedEdits',
    { count: edits, ns: 'app' },
  )
}

/**
 * QS credit chip on the same parent/read basis as `buildRunSpendLine`.
 * Docked `ThreadRunGate` wires this once audience tier is known.
 */
export function buildRunGateCreditChip(
  summary: RunGateSpendSummary | null,
  audienceTier: string,
  quickStudyCreditsPerRead?: number,
): ReactNode {
  if (!summary || summary.questions === 0) {
    return null
  }

  /* When the spend sentence hedges ("up to N twins"), a definite credit chip
     priced at the ceiling would over-claim. Omit rather than invent certainty. */
  if (!summary.isKnownPanel) {
    return null
  }

  const { reads, credits } = computeRunGateCreditCost(
    summary,
    audienceTier,
    quickStudyCreditsPerRead,
  )
  /* An unusable price (not a finite number) is omitted, never rendered as 0 or free. */
  if (!Number.isFinite(credits)) {
    return null
  }
  const label = i18n.t('threads.detail.gate.credit', { count: credits, ns: 'app' })
  const breakdown = i18n.t('threads.detail.gate.creditBreakdown', {
    reads,
    ns: 'app',
  })

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground select-none"
            data-testid="run-gate-credit-chip"
          >
            <CreditIcon className="h-3 w-3" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px] text-center text-xs">
          {label} · {breakdown}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function RunGateCard(props: RunGateCardProps) {
  const {
    step,
    artifacts,
    actionable,
    canEdit,
    viewerOwnsRun,
    spendLine,
    editState,
    creditChip,
    segmentSlot = null,
    approveBlocked = false,
    stimulusRefs = [],
    maxQuestions,
    chrome = 'card',
    nextStepTitle = null,
    language = null,
    onSubmit,
    onCancelRun,
  } = props

  const { t } = useTranslation('app')

  // Always called, sometimes ignored: the hook rule holds either way, and the
  // fallback is what the watch-only mount runs on.
  const ownEditState = useGateEditState(step.stepId)
  const { edits, editingKey, startEdit, cancelEdit, saveEdit, stageEdit } =
    editState ?? ownEditState

  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  // Latches on the first approve so a double click cannot send a second
  // payload while the run's own state is still catching up.
  const [submitted, setSubmitted] = useState(false)

  const isPending = step.state === 'awaiting_approval'

  function bodyOf(artifact: GateArtifactEntry): string {
    return edits[artifact.key] ?? artifact.body
  }

  /** Peer questions across typed payloads (empty list blocks Approve). */
  let gateQuestionCount = 0
  let hasQuestionsPayload = false
  for (const artifact of artifacts) {
    const parsed = parseGateQuestions(bodyOf(artifact))
    if (parsed === null) {
      continue
    }
    hasQuestionsPayload = true
    gateQuestionCount += parsed.length
  }
  /* Prose already lives in the thread. Hide it on the deciding mount so a
     scan / brief / report gate is just spend + Continue, not a second copy
     of the artifact. Questions stay: they are the thing being approved. */
  const hideProseBodies = actionable && isPending
  const canApproveQuestions =
    !hasQuestionsPayload || gateQuestionCount > 0
  /* Media compare only: N≥2 bound refs with the same file more than once. */
  const stimulusDuplicateBlocked = hasDuplicateStimulusMedia(stimulusRefs)
  const approveDisabled =
    !viewerOwnsRun ||
    !canApproveQuestions ||
    stimulusDuplicateBlocked ||
    approveBlocked

  function toggleExpand(artifact: GateArtifactEntry) {
    // One expanded at a time: a second open closes the first, so the pane is a
    // list with one thing being read rather than seven stacked documents.
    setExpandedKey((current) => (current === artifact.key ? null : artifact.key))
    cancelEdit()
  }

  const visibleEntries: Array<{ artifact: GateArtifactEntry; position: number }> =
    artifacts
      .map((artifact, position) => ({ artifact, position }))
      .filter(({ artifact }) => {
        if (!hideProseBodies) {
          return true
        }
        return isGateQuestionsDockBody(bodyOf(artifact))
      })

  function handleApprove() {
    if (
      submitted ||
      !viewerOwnsRun ||
      !actionable ||
      !canApproveQuestions ||
      stimulusDuplicateBlocked ||
      approveBlocked
    ) {
      return
    }
    setSubmitted(true)
    onSubmit({
      stepId: step.stepId,
      decision: 'approve',
      // `stepKey` travels with every edit: the server takes gate edits as one
      // full replacement output PER PRODUCING STEP, and this list is flattened
      // across several of them.
      edits: artifacts
        .filter((artifact) => edits[artifact.key] !== undefined)
        .map((artifact) => ({
          stepKey: artifact.stepKey,
          slug: artifact.slug,
          index: artifact.index,
          body: edits[artifact.key],
        })),
    })
  }

  const cards =
    visibleEntries.length > 0 ? (
      <ul className="space-y-2" data-testid="run-gate-artifacts">
        {visibleEntries.map(({ artifact, position }) => (
          <RunGateArtifactCard
            key={artifact.key}
            artifact={artifact}
            position={position}
            body={bodyOf(artifact)}
            isEdited={edits[artifact.key] !== undefined}
            isExpanded={expandedKey === artifact.key}
            isEditing={editingKey === artifact.key}
            canEdit={actionable && canEdit && viewerOwnsRun && isPending && !submitted}
            onToggleExpand={() => toggleExpand(artifact)}
            onStartEdit={() => startEdit(artifact.key)}
            onSaveEdit={(body) => saveEdit(artifact, body)}
            onStageEdit={(body) => stageEdit(artifact, body)}
            onCancelEdit={() => cancelEdit()}
            stimulusRefs={stimulusRefs}
            maxQuestions={maxQuestions}
            language={language}
          />
        ))}
      </ul>
    ) : null

  const ownerOnlyNotice = viewerOwnsRun ? null : (
    <Text size="xs" color="muted" data-testid="run-gate-owner-only">
      {t('threads.detail.gate.ownerOnly')}
    </Text>
  )
  const stimulusDuplicateNotice = stimulusDuplicateBlocked ? (
    <Text
      size="xs"
      color="muted"
      data-testid="run-gate-stimulus-duplicate"
    >
      {t('threads.detail.gate.duplicateStimulus')}
    </Text>
  ) : null

  return (
    <div
      className={
        chrome === 'inline'
          ? undefined
          : 'mt-2 rounded-2xl border border-primary/20 bg-background p-4 sm:p-5'
      }
      data-testid="run-gate"
      data-state={isPending ? 'pending' : 'decided'}
    >
      {isPending ? null : (
        <Text
          size="xs"
          color="muted"
          className="px-1 pb-2"
          data-testid="run-gate-decision"
        >
          {decisionSummary(step)}
        </Text>
      )}

      {cards}

      {isPending ? segmentSlot : null}

      {isPending && !actionable ? (
        <Text
          size="xs"
          color="muted"
          className="px-1 pt-2"
          data-testid="run-gate-decide-in-thread"
        >
          {/* `buildRunSpendLine` never ends in a period, so the sentence break
              belongs here rather than trailing off the spend line. */}
          {spendLine
            ? t('threads.detail.gate.approveInThread', { spendLine })
            : t('threads.detail.gate.continueInThread')}
        </Text>
      ) : null}

      {actionable && isPending && !submitted ? (
        chrome === 'inline' ? (
          <div className="mt-2 space-y-2">
            {ownerOnlyNotice}
            {stimulusDuplicateNotice}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={approveDisabled}
                onClick={handleApprove}
                data-testid="run-gate-approve"
              >
                {spendLine
                  ? t('threads.detail.gate.approveAndRun')
                  : nextStepTitle || t('threads.detail.gate.continue')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!viewerOwnsRun}
                onClick={onCancelRun}
                data-testid="run-gate-cancel"
              >
                {t('threads.detail.gate.cancelRun')}
              </Button>
              {spendLine ? (
                <Text size="xs" color="muted" data-testid="run-gate-spend">
                  {spendLine}
                </Text>
              ) : null}
              {creditChip ? (
                <div className="shrink-0" data-testid="run-gate-credit-chip-slot">
                  {creditChip}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          /* Sticky, because the decision must stay reachable while the reviewer
             scrolls a long artifact: an approve button that scrolls away is an
             approve button people click without reading the spend line. */
          <div className="sticky bottom-0 mt-2 space-y-2 border-t border-border/50 bg-background/95 px-1 pb-2 pt-2 backdrop-blur">
            {ownerOnlyNotice}
            {stimulusDuplicateNotice}
            {/* Spend rides the decision row rather than a band of its own:
                it is the thing the buttons are priced by, and two stacked
                rows cost a whole line of the docked card's height. The spend
                text truncates so a long sentence never pushes the decision
                off the edge. */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                {spendLine ? (
                  <Text
                    size="xs"
                    color="muted"
                    className="min-w-0"
                    data-testid="run-gate-spend"
                  >
                    {spendLine}
                  </Text>
                ) : null}
                {creditChip ? (
                  <div className="shrink-0" data-testid="run-gate-credit-chip-slot">
                    {creditChip}
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  disabled={!viewerOwnsRun}
                  onClick={onCancelRun}
                  data-testid="run-gate-cancel"
                >
                  {t('threads.detail.gate.cancelRun')}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="h-8 px-3 text-xs"
                  disabled={approveDisabled}
                  onClick={handleApprove}
                  data-testid="run-gate-approve"
                >
                  {spendLine
                    ? t('threads.detail.gate.approveAndRun')
                    : nextStepTitle || t('threads.detail.gate.continue')}
                </Button>
              </div>
            </div>
          </div>
        )
      ) : null}
    </div>
  )
}
